import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import HotReplyAlertsCard from "../components/HotReplyAlertsCard";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Inbox,
  Flame,
  Check,
  RefreshCcw,
  MailQuestion,
  ShieldOff,
  Clock,
  AlertCircle,
  Copy,
  Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTENT_META = {
  interested:     { label: "Interested", color: "bg-green-100 text-green-700", icon: Flame },
  question:       { label: "Question", color: "bg-blue-100 text-blue-700", icon: MailQuestion },
  objection:      { label: "Objection", color: "bg-amber-100 text-amber-700", icon: AlertCircle },
  not_interested: { label: "Not interested", color: "bg-slate-100 text-slate-600", icon: ShieldOff },
  please_stop:    { label: "Unsubscribe", color: "bg-red-100 text-red-700", icon: ShieldOff },
  out_of_office:  { label: "OOO (paused 14d)", color: "bg-purple-100 text-purple-700", icon: Clock },
  other:          { label: "Other", color: "bg-slate-100 text-slate-500", icon: Inbox },
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (new Date() - d) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  } catch { return iso; }
}

export default function CampaignRepliesPage() {
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterHandled, setFilterHandled] = useState("unhandled");
  const [selected, setSelected] = useState(null);

  const fetchReplies = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (filterHandled === "handled") qs.set("handled", "true");
      if (filterHandled === "unhandled") qs.set("handled", "false");
      const res = await fetch(`${API}/campaign-replies?${qs}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setReplies(data.replies || []);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReplies(); }, [filterHandled]);  // eslint-disable-line

  const markHandled = async (reply_id) => {
    const res = await fetch(`${API}/campaign-replies/${reply_id}/handled`, {
      method: "PATCH", credentials: "include",
    });
    if (res.ok) {
      toast.success("Marked handled");
      setSelected(null);
      fetchReplies();
    }
  };

  const counts = {
    hot: replies.filter(r => r.intent === "interested" && !r.handled).length,
    total: replies.length,
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6 animate-fade-in-up" data-testid="campaign-replies-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Inbox className="w-6 h-6 text-blue-600" />
            Campaign Replies
          </h1>
          <p className="text-slate-500 mt-1">
            Inbound replies — auto-classified by AI. Hot leads pause the sequence and wait for you.
          </p>
        </div>
        <div className="flex gap-2">
          <select
            value={filterHandled}
            onChange={(e) => setFilterHandled(e.target.value)}
            className="h-9 px-3 rounded-md border border-slate-200 bg-white text-sm"
            data-testid="replies-filter"
          >
            <option value="unhandled">Needs review</option>
            <option value="handled">Handled</option>
            <option value="all">All</option>
          </select>
          <Button variant="outline" onClick={fetchReplies}>
            <RefreshCcw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {counts.hot > 0 && (
        <Card className="border-green-200 bg-green-50/40" data-testid="hot-replies-banner">
          <CardContent className="p-4 flex items-center gap-3">
            <Flame className="w-5 h-5 text-green-600" />
            <div className="flex-1">
              <p className="font-semibold text-green-900">
                {counts.hot} interested {counts.hot === 1 ? "reply" : "replies"} need your response
              </p>
              <p className="text-sm text-green-700">
                The AI detected buying signals. Their campaign is paused so you can reply personally.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {counts.total} {counts.total === 1 ? "reply" : "replies"}
          </CardTitle>
          <CardDescription className="text-xs">
            AI classification runs when replies hit the webhook
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
          ) : replies.length === 0 ? (
            <div className="text-center py-12 text-sm text-slate-500">
              <Inbox className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              No replies in this view.
              <p className="text-xs mt-2 text-slate-400">
                Configure your inbound webhook at the URL below to start receiving classifications.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {replies.map((r) => {
                const meta = INTENT_META[r.intent] || INTENT_META.other;
                const Icon = meta.icon;
                return (
                  <button
                    key={r.reply_id}
                    onClick={() => setSelected(r)}
                    className="w-full text-left px-4 py-3 hover:bg-slate-50"
                    data-testid={`reply-row-${r.reply_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4 text-slate-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <span className="font-medium text-sm text-slate-900">{r.lead_name}</span>
                          <Badge className={`${meta.color} text-[10px]`}>{meta.label}</Badge>
                          {!r.handled && r.intent === "interested" && (
                            <Badge className="bg-green-600 text-white text-[10px]">HOT</Badge>
                          )}
                          {r.handled && (
                            <Badge variant="outline" className="text-[10px]">handled</Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 truncate">
                          {r.campaign_name} · {r.from_email}
                        </p>
                        <p className="text-xs text-slate-600 truncate mt-0.5">{r.summary}</p>
                      </div>
                      <span className="text-[10px] text-slate-400 whitespace-nowrap">
                        {formatDate(r.received_at)}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Hot reply alerts config */}
      <HotReplyAlertsCard />

      {/* Setup instructions */}
      <Card className="border-slate-200 bg-slate-50/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Inbound Webhook Setup (one-time)</CardTitle>
          <CardDescription className="text-xs">
            Point your email provider&apos;s inbound parse webhook to the URL below. Replies will auto-classify with AI.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-xs space-y-2">
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded p-2">
            <code className="flex-1 font-mono break-all" data-testid="webhook-url">
              {process.env.REACT_APP_BACKEND_URL}/api/campaign-inbound
            </code>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/campaign-inbound`);
                toast.success("Webhook URL copied");
              }}
            >
              <Copy className="w-3.5 h-3.5" />
            </Button>
          </div>
          <p className="text-slate-500">
            <strong>SendGrid:</strong> Settings → Inbound Parse → Add Host &amp; URL → paste this URL, pick your domain.<br />
            <strong>Mailgun:</strong> Routes → Create → Expression <code>match_recipient(&quot;reply@yourdomain&quot;)</code> → Action: Forward to this URL.<br />
            <strong>Testing:</strong> POST JSON to the URL with <code>{`{ "from": "...", "to": "...", "subject": "...", "body": "..." }`}</code>.
          </p>
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="reply-detail">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Reply from {selected?.lead_name}
              {selected && (
                <Badge className={(INTENT_META[selected.intent] || INTENT_META.other).color}>
                  {(INTENT_META[selected.intent] || INTENT_META.other).label}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {selected?.campaign_name} · {selected?.from_email} · confidence {((selected?.confidence || 0) * 100).toFixed(0)}%
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2 text-sm">
            <div>
              <div className="text-xs text-slate-500 mb-1">AI summary</div>
              <div className="p-3 rounded-md bg-blue-50 border border-blue-200 text-slate-800">
                {selected?.summary}
              </div>
            </div>

            <div>
              <div className="text-xs text-slate-500 mb-1">Subject</div>
              <div className="font-medium">{selected?.subject || "(no subject)"}</div>
            </div>

            <div>
              <div className="text-xs text-slate-500 mb-1">Full reply</div>
              <div className="p-3 rounded-md bg-slate-50 border border-slate-200 whitespace-pre-wrap break-words max-h-64 overflow-y-auto">
                {selected?.body}
              </div>
            </div>

            {selected?.suggested_reply && (
              <div>
                <div className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                  AI-suggested response
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2"
                    onClick={() => {
                      navigator.clipboard.writeText(selected.suggested_reply);
                      toast.success("Copied — paste into your email client");
                    }}
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    Copy
                  </Button>
                </div>
                <div className="p-3 rounded-md bg-green-50 border border-green-200 whitespace-pre-wrap break-words">
                  {selected.suggested_reply}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex-row gap-2 justify-between">
            {selected?.lead_id && (
              <a href={`/leads/${selected.lead_id}`}>
                <Button variant="outline">Open Lead</Button>
              </a>
            )}
            <div className="flex gap-2">
              {!selected?.handled && (
                <Button onClick={() => markHandled(selected.reply_id)} data-testid="mark-handled-btn">
                  <Check className="w-4 h-4 mr-2" />
                  Mark handled
                </Button>
              )}
              <Button variant="outline" onClick={() => setSelected(null)}>Close</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
